import { useRef, useMemo } from "react";
import { useForm } from "../../context/FormContext";
import { useLang } from "../../context/LanguageContext";
import type { FormData, DateValue } from "../../context/FormContext";
import {
  validateDateValueParts,
  isDatePartsComplete,
  type DatePartsErrorKey,
} from "../../utils/datePartsValidation";

interface Props {
  field: keyof FormData;
  label?: string;
  helper?: string;
  optionalLabel?: string;
  dayPlaceholder?: string;
  monthPlaceholder?: string;
  yearPlaceholder?: string;
  /** Date must not be after today (e.g. DOB, arrival). */
  disallowFuture?: boolean;
  /** Date must be today or later. */
  requireFutureOrToday?: boolean;
  /** No min/max year check — only month (01–12) and calendar day validity. */
  skipYearRange?: boolean;
  /** Day/month 1–2 digits, numeric (arrival-style). */
  flexDayMonthDigits?: boolean;
  isWarning?: boolean;
  minYear?: number;
  maxYear?: number;
}

export default function DateInput({
  field,
  label,
  helper,
  optionalLabel,
  dayPlaceholder = "DD",
  monthPlaceholder = "MM",
  yearPlaceholder = "AAAA",
  disallowFuture = false,
  requireFutureOrToday = false,
  skipYearRange = false,
  flexDayMonthDigits = false,
  isWarning = false,
  minYear: minYearProp,
  maxYear: maxYearProp,
}: Props) {
  const { t } = useLang();
  const { data, update } = useForm();
  const val = (data[field] as DateValue) ?? { day: "", month: "", year: "" };

  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const currentCalendarYear = new Date().getFullYear();
  const minYear =
    minYearProp ?? (requireFutureOrToday ? currentCalendarYear - 100 : 1900);
  const maxYear =
    maxYearProp ??
    (requireFutureOrToday
      ? currentCalendarYear + 50
      : disallowFuture
        ? currentCalendarYear
        : 2100);

  const validationOptions = useMemo(() => {
    const flex = flexDayMonthDigits ? { flexDayMonthDigits: true as const } : {};
    return skipYearRange
      ? {
          skipYearRange: true as const,
          disallowFuture,
          requireFutureOrToday,
          ...flex,
        }
      : {
          disallowFuture,
          requireFutureOrToday,
          minYear,
          maxYear,
          ...flex,
        };
  }, [skipYearRange, disallowFuture, requireFutureOrToday, minYear, maxYear, flexDayMonthDigits]);

  const validationError = useMemo((): DatePartsErrorKey | null => {
    return validateDateValueParts(val.day, val.month, val.year, validationOptions);
  }, [val.day, val.month, val.year, validationOptions]);

  const handleDay = (v: string) => {
    const clean = v.replace(/\D/g, "").slice(0, 2);
    update(field, { ...val, day: clean });
    if (clean.length === 2) monthRef.current?.focus();
  };

  const handleMonth = (v: string) => {
    const clean = v.replace(/\D/g, "").slice(0, 2);
    update(field, { ...val, month: clean });
    if (clean.length === 2) yearRef.current?.focus();
  };

  const handleYear = (v: string) => {
    const clean = v.replace(/\D/g, "").slice(0, 4);
    update(field, { ...val, year: clean });
  };

  const complete = isDatePartsComplete(val.day, val.month, val.year);
  const isValid = complete && !validationError;
  const showError = !!validationError;

  const errorMessage = validationError ? t(`validation.${validationError}`) : "";

  return (
    <div id={`field-${String(field)}`}>
      {label && (
        <div className="flex items-center gap-2 mb-1.5">
          <label className="text-[13px] font-semibold text-slate-500 uppercase tracking-wide">
            {label}
          </label>
          {optionalLabel && (
            <span className="text-[11px] text-slate-400 bg-slate-50 border border-slate-100 rounded-full px-2 py-0.5">
              {optionalLabel}
            </span>
          )}
        </div>
      )}
      <div className="overflow-hidden w-full" style={{ boxSizing: "border-box" }}>
        <div
          className={`flex items-center bg-white border rounded-xl overflow-hidden transition-colors ${
            showError
              ? "border-rose-400 focus-within:border-rose-500"
              : isValid
                ? isWarning
                  ? "border-emerald-400"
                  : "border-emerald-400"
                : "border-slate-200 focus-within:border-emerald-400"
          }`}
          style={{ maxWidth: "min(280px, 100%)" }}
        >
          <input
            type="text"
            inputMode="numeric"
            value={val.day}
            onChange={(e) => handleDay(e.target.value)}
            placeholder={dayPlaceholder}
            maxLength={2}
            style={{ fontSize: "16px" }}
            aria-invalid={showError}
            className="flex-1 min-w-0 h-12 text-center text-slate-900 placeholder:text-slate-300 bg-transparent border-0 focus:border-0 focus:ring-0 focus:outline-none"
          />
          <span className="w-4 flex-shrink-0 text-center text-slate-300 font-light text-lg">/</span>
          <input
            ref={monthRef}
            type="text"
            inputMode="numeric"
            value={val.month}
            onChange={(e) => handleMonth(e.target.value)}
            placeholder={monthPlaceholder}
            maxLength={2}
            style={{ fontSize: "16px" }}
            aria-invalid={showError}
            className="flex-1 min-w-0 h-12 text-center text-slate-900 placeholder:text-slate-300 bg-transparent border-0 focus:border-0 focus:ring-0 focus:outline-none"
          />
          <span className="w-4 flex-shrink-0 text-center text-slate-300 font-light text-lg">/</span>
          <input
            ref={yearRef}
            type="text"
            inputMode="numeric"
            value={val.year}
            onChange={(e) => handleYear(e.target.value)}
            placeholder={yearPlaceholder}
            maxLength={4}
            style={{ fontSize: "16px" }}
            aria-invalid={showError}
            className="flex-[1.2] min-w-0 h-12 text-center text-slate-900 placeholder:text-slate-300 bg-transparent border-0 focus:border-0 focus:ring-0 focus:outline-none"
          />
          {isValid && (
            <div className="pr-2 flex-shrink-0">
              {isWarning ? (
                <i className="ri-error-warning-line text-amber-400" />
              ) : (
                <i className="ri-checkbox-circle-fill text-emerald-500" />
              )}
            </div>
          )}
        </div>
      </div>
      {showError && <p className="text-[12px] text-rose-500 mt-1">{errorMessage}</p>}
      {helper && !showError && <p className="text-[13px] text-slate-400 mt-1">{helper}</p>}
    </div>
  );
}
